'use client';

import { useLocale, useTranslations } from 'next-intl';
import type { MatchWithRating } from './types';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate, formatRating, formatTime } from '@/lib/utils';
import { TableRow, TableCell, ScoreChange, Link } from '@/components/ui';
import PlayerCard from '@/components/player/PlayerCard';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { Badge } from '@/components/ui/badge';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { useOutcomeLabel } from './matchOutcomeLabel';
import { completedGamesWon, formatScoreLine } from './formatScoreLine';
import { VENUE_COURT_SEPARATOR } from '@/app/[locale]/event/[eventId]/libs/venues';

interface MatchRowProps {
  match: MatchWithRating;
  showBothPlayers?: boolean;
  showDate?: boolean;
  showRatings?: boolean;
  player1?: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_photo: string | null;
    current_rating: number | null;
    rating_delta?: number | null;
  } | null;
  player2?: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_photo: string | null;
    current_rating: number | null;
    rating_delta?: number | null;
  } | null;
}

/** A multi-venue court label ("Venue · Court") may break only at the dot, never inside a half. */
function renderVenueLabel(venue: string | null | undefined) {
  if (!venue || !venue.includes(VENUE_COURT_SEPARATOR)) return venue;
  const parts = venue.split(VENUE_COURT_SEPARATOR);
  return parts.map((part, i) => (
    <span key={i}>
      {i > 0 && VENUE_COURT_SEPARATOR}
      <span className="whitespace-nowrap">{part}</span>
    </span>
  ));
}

export function MatchRow({
  match,
  showBothPlayers = false,
  showDate = true,
  showRatings = true,
  player1,
  player2,
}: MatchRowProps) {
  const {
    dateISO,
    opponent,
    opponentUsername,
    opponentProfilePhoto,
    venue,
    competition,
    myRating,
    myDelta,
    oppRating,
    oppDelta,
    isAdjustment,
    gameScores,
    hasScorecard,
    isWalkover,
    outcome,
    retiredPlayerId,
    matchStatus,
    appState,
    hideRating,
    hideProfileLink,
    player1Roster,
    opponentRoster,
  } = match;

  const locale = useLocale();
  const t = useTranslations('Matches');
  const outcomeLabel = useOutcomeLabel();

  // Determine if match is live
  const winnerless = isWinnerlessRow({ outcome, is_walkover: isWalkover });
  const isLive = !winnerless && matchStatus === 'in_progress';
  const isCompleted = !winnerless && matchStatus === 'completed';
  const isScheduled =
    !winnerless && (!matchStatus || matchStatus === 'scheduled' || (!isLive && !isCompleted));

  const retired = outcome === 'retired' || !!retiredPlayerId;

  // Get live scores if available
  let liveScores = gameScores;
  let gamesWon = { player1: 0, player2: 0 };

  const resolved = isLive ? tryResolveAppState(appState) : null;
  if (resolved && resolved.games.length > 0) {
    liveScores = resolved.games.map((g) => ({
      player1: g.player_1_score,
      player2: g.player_2_score,
    }));
    gamesWon = { player1: resolved.player_1_games, player2: resolved.player_2_games };
  } else if (liveScores && liveScores.length > 0) {
    // Completed games only: a retired match's unfinished game is shown but never counted.
    gamesWon = completedGamesWon(liveScores);
  }

  return (
    <TableRow className={isAdjustment ? 'bg-amber-50/70 dark:bg-amber-900/10' : ''}>
      {showDate && (
        <TableCell className="text-xs md:text-sm">
          {/* `dateISO` is a timestamptz — see MatchCard. */}
          <LocalDateTime iso={dateISO} locale={locale} format={formatDate} />
        </TableCell>
      )}
      {showBothPlayers ? (
        <>
          <TableCell className="text-xs md:text-sm">
            <LocalDateTime iso={dateISO} locale={locale} format={formatTime} />
          </TableCell>
          <TableCell>
            {player1 ? (
              <>
                <PlayerCard
                  player={{
                    id: player1.id,
                    username: player1.username,
                    first_name: player1.first_name,
                    last_name: player1.last_name,
                    profile_photo: player1.profile_photo,
                    current_rating: player1.current_rating,
                  }}
                  showRating={false}
                  showUsername={!hideProfileLink}
                  size="compact"
                  clickable={!hideProfileLink}
                  className="md:h-8"
                />
                {player1Roster && (
                  <div className="text-muted truncate text-xs">{player1Roster}</div>
                )}
              </>
            ) : (
              <div className="text-muted text-xs">{t('tbd')}</div>
            )}
          </TableCell>
          <TableCell>
            {opponent && opponent !== t('tbd') ? (
              <>
                <PlayerCard
                  player={{
                    id: player2?.id || opponentUsername || 'unknown',
                    username: player2?.username || opponentUsername,
                    first_name:
                      player2?.first_name ||
                      (hideProfileLink ? opponent : opponent ? opponent.split(' ')[0] : ''),
                    last_name:
                      player2?.last_name ||
                      (hideProfileLink || !opponent ? '' : opponent.split(' ').slice(1).join(' ')),
                    profile_photo: player2?.profile_photo || opponentProfilePhoto,
                    current_rating: player2?.current_rating || oppRating,
                  }}
                  showRating={false}
                  showUsername={!hideProfileLink}
                  size="compact"
                  clickable={!hideProfileLink}
                  className="md:h-8"
                />
                {opponentRoster && (
                  <div className="text-muted truncate text-xs">{opponentRoster}</div>
                )}
              </>
            ) : (
              <div className="text-muted text-xs">{t('tbd')}</div>
            )}
          </TableCell>
        </>
      ) : (
        <TableCell>
          {isAdjustment ? (
            <div className="flex items-center gap-2 md:gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-medium text-amber-700 md:h-8 md:w-8 md:text-[11px] dark:bg-amber-300/15 dark:text-amber-300">
                ADJ
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs md:text-sm">
                  <span className="text-foreground/80">{t('rankingAdjustment')}</span>
                </div>
              </div>
            </div>
          ) : opponent && opponent !== t('tbd') ? (
            <>
              <PlayerCard
                player={{
                  id: player2?.id || opponentUsername || 'unknown',
                  username: player2?.username || opponentUsername,
                  first_name:
                    player2?.first_name ||
                    (hideProfileLink ? opponent : opponent ? opponent.split(' ')[0] : ''),
                  last_name:
                    player2?.last_name ||
                    (hideProfileLink || !opponent ? '' : opponent.split(' ').slice(1).join(' ')),
                  profile_photo: player2?.profile_photo || opponentProfilePhoto,
                  current_rating: player2?.current_rating || oppRating,
                }}
                showRating={false}
                showUsername={!hideProfileLink}
                size="compact"
                clickable={!hideProfileLink}
                className="md:h-8"
              />
              {opponentRoster && (
                <div className="text-muted truncate text-xs">{opponentRoster}</div>
              )}
            </>
          ) : (
            <div className="text-muted text-xs">{t('tbd')}</div>
          )}
        </TableCell>
      )}
      {/* Hide competition on smaller screens */}
      <TableCell className="hidden lg:table-cell">
        <div className="text-xs md:text-sm">
          {competition === 'Ranking Adjustment' ? t('rankingAdjustment') : competition}
          {/* {competitionDivision && <span className="text-muted ml-1">• {competitionDivision}</span>} */}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          {isLive && (
            <Badge variant="destructive" className="w-fit animate-pulse px-1.5 py-0.5 text-[10px]">
              LIVE
            </Badge>
          )}
          {isScheduled && !isLive && (
            <Badge variant="outline" className="w-fit px-1.5 py-0.5 text-[10px]">
              SCHEDULED
            </Badge>
          )}
          {isCompleted && !isLive && (
            <Badge variant="secondary" className="w-fit px-1.5 py-0.5 text-[10px]">
              COMPLETED
            </Badge>
          )}
          {winnerless && (
            <div className="text-muted text-sm font-medium">
              {outcomeLabel(outcome, !!isWalkover)}
            </div>
          )}
          {(isLive || isCompleted) && !winnerless && liveScores && liveScores.length > 0 && (
            <div className="text-sm">
              <span className="font-bold">
                {gamesWon.player1} - {gamesWon.player2}
              </span>
              <span className="text-muted ml-1 text-xs">
                (
                {formatScoreLine(
                  liveScores.map((s) => ({ player1: s.player1, player2: s.player2 })),
                  { retired, marker: t('retiredShort') },
                )}
                )
              </span>
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="text-xs md:text-sm">{renderVenueLabel(venue)}</div>
      </TableCell>
      {showRatings &&
        (showBothPlayers ? (
          <>
            <TableCell className="text-right">
              {!hideRating && (
                <div className="flex flex-col items-end">
                  {player1?.current_rating && (
                    <span className="text-sm font-medium">
                      {formatRating(player1.current_rating)}
                    </span>
                  )}
                  {player1?.rating_delta !== null && player1?.rating_delta !== undefined && (
                    <ScoreChange
                      value={player1.rating_delta}
                      isPositive={player1.rating_delta > 0}
                    />
                  )}
                </div>
              )}
            </TableCell>
            <TableCell className="hidden text-right sm:table-cell">
              {!hideRating && (
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{formatRating(oppRating)}</span>
                  {oppDelta !== null && <ScoreChange value={oppDelta} isPositive={oppDelta > 0} />}
                </div>
              )}
            </TableCell>
          </>
        ) : (
          <>
            <TableCell className="text-right">
              {!hideRating && (
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{formatRating(myRating)}</span>
                  {myDelta !== null && <ScoreChange value={myDelta} isPositive={myDelta > 0} />}
                </div>
              )}
            </TableCell>
            <TableCell className="hidden text-right sm:table-cell">
              {!hideRating && (
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{formatRating(oppRating)}</span>
                  {oppDelta !== null && <ScoreChange value={oppDelta} isPositive={oppDelta > 0} />}
                </div>
              )}
            </TableCell>
          </>
        ))}
      <TableCell className="hidden text-center sm:table-cell">
        {(hasScorecard || isLive) && (
          <Link href={`/match/scorecard/${match.id}`} variant="filled" color="default" size="sm">
            {t('details')}
          </Link>
        )}
      </TableCell>
    </TableRow>
  );
}
